import React, { useState, useMemo } from 'react';
import { fmtQty, fmtNum } from './fmt';
import { useSortableRows, SortableTh } from '../../../components/tables/SortableTable';

// Ledger vs Tracker vs Broker reconciliation. Collapsed by default (it's a
// diagnostic, not an everyday glance). The Broker/Manual columns need the live
// IBKR read, so we LAZY-load it on first expand via onExpand — the page itself
// never calls the gateway. Reconciles as: Tracker + Reserve + Central + Manual = Broker
// (Central = the account's central-book hedge basket, e.g. the Genesis −100 SPY short —
// our shares, owned by no strategy; before 2026-10-05 it was mis-shown as Manual).
export default function ReconcileTable({ rows, brokerBySymbol, brokerLoading, onExpand, loading }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const list = useMemo(() => rows || [], [rows]);
  const broker = brokerBySymbol || null;
  // Broker/Manual accessors need the live broker map, so columns close over it.
  const reconCols = useMemo(() => [
    { key: 'symbol', type: 'string', get: (r) => r.symbol },
    { key: 'ledger', type: 'number', get: (r) => r.ledger_qty },
    { key: 'tracker', type: 'number', get: (r) => r.tracker_qty },
    { key: 'reserve', type: 'number', get: (r) => r.reserve_qty },
    { key: 'central', type: 'number', get: (r) => r.central_qty || 0 },
    { key: 'broker', type: 'number', get: (r) => (broker?.[r.symbol] ?? 0) },
    { key: 'manual', type: 'number', get: (r) => {
      const bq = broker?.[r.symbol];
      return bq == null ? 0 : bq - ((r.tracker_qty || 0) + (r.reserve_qty || 0) + (r.central_qty || 0));
    } },
  ], [broker]);
  const { sorted: reconRows, toggle: sortCol, indicator } = useSortableRows(list, reconCols);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && onExpand) onExpand(); // fire the lazy broker fetch on first open
  };
  const toggleRow = (sym) => setExpanded((e) => ({ ...e, [sym]: !e[sym] }));

  const totals = useMemo(() => {
    let l = 0, t = 0, res = 0, cen = 0, b = 0;
    list.forEach((r) => {
      l += r.ledger_qty || 0;
      t += r.tracker_qty || 0;
      res += r.reserve_qty || 0;
      cen += r.central_qty || 0;
      b += (broker?.[r.symbol]) || 0;
    });
    return { l, t, res, cen, b, sys: t + res + cen };
  }, [list, broker]);

  const amber = 'text-amber-500 dark:text-amber-400 font-semibold';
  const brokerCell = (v) => (brokerLoading && broker == null ? '…' : v == null ? '—' : fmtNum(v, 0));
  const mismTotal = Math.abs(totals.l - totals.t) > 1e-4;

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
      <button onClick={handleToggle} className="w-full flex items-center justify-between gap-2 text-left">
        <h3 className="text-sm font-serif text-gray-800 dark:text-gray-200">
          Positions — Ledger vs Tracker vs Broker <span className="text-gray-400 text-xs">({list.length})</span>
        </h3>
        <span className="text-[11px] text-gray-400 shrink-0">{open ? '▾ Hide' : '▸ Show'}</span>
      </button>

      {open && (
        <>
          <p className="text-[10.5px] text-gray-400 mt-2 mb-3 leading-relaxed">
            <b className="text-gray-500 dark:text-gray-300">Ledger</b> = realized-fill replay ·{' '}
            <b className="text-gray-500 dark:text-gray-300">Tracker</b> = live Position book{' '}
            <span className="text-amber-500 dark:text-amber-400">(must match — amber = a real bug)</span> ·{' '}
            <b className="text-gray-500 dark:text-gray-300">Reserve</b> = sub-share make-whole ·{' '}
            <b className="text-gray-500 dark:text-gray-300">Central</b> = central-book hedge (ours, no strategy) ·{' '}
            <b className="text-gray-500 dark:text-gray-300">Broker</b> = IBKR live ·{' '}
            <b className="text-gray-500 dark:text-gray-300">Manual</b> = Broker − (Tracker+Reserve+Central).{' '}
            Reconciles as <span className="text-brand-gold">Tracker + Reserve + Central + Manual = Broker</span>. Tap a row for detail.
          </p>

          {loading && !list.length ? (
            <div className="space-y-2 py-2">
              {[...Array(4)].map((_, i) => <div key={i} className="h-7 skeleton rounded-md" />)}
            </div>
          ) : !list.length ? (
            <div className="text-xs text-gray-400 italic py-4">No strategy positions for this account.</div>
          ) : (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-[12px]" style={{ tableLayout: 'fixed', minWidth: 520 }}>
                <colgroup><col style={{ width: '22%' }} /><col style={{ width: '14%' }} /><col style={{ width: '14%' }} /><col style={{ width: '13%' }} /><col style={{ width: '12%' }} /><col style={{ width: '12%' }} /><col style={{ width: '13%' }} /></colgroup>
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
                    <SortableTh label="Symbol" colKey="symbol" align="left" className="py-2" toggle={sortCol} indicator={indicator} />
                    <SortableTh label="Ledger" colKey="ledger" className="py-2" toggle={sortCol} indicator={indicator} />
                    <SortableTh label="Tracker" colKey="tracker" className="py-2" toggle={sortCol} indicator={indicator} />
                    <SortableTh label="Reserve" colKey="reserve" className="py-2" toggle={sortCol} indicator={indicator} />
                    <SortableTh label="Central" colKey="central" className="py-2" toggle={sortCol} indicator={indicator} />
                    <SortableTh label="Broker" colKey="broker" className="py-2" toggle={sortCol} indicator={indicator} />
                    <SortableTh label="Manual" colKey="manual" className="py-2" toggle={sortCol} indicator={indicator} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
                  {reconRows.map((r) => {
                    const bq = broker?.[r.symbol];
                    const central = r.central_qty || 0;
                    const sysNet = (r.tracker_qty || 0) + (r.reserve_qty || 0) + central;   // our net incl. the hedge
                    const manual = bq != null ? bq - sysNet : null;
                    const drift = (r.ledger_qty || 0) - (r.tracker_qty || 0);
                    const mism = Math.abs(drift) > 1e-4;
                    const isOpen = !!expanded[r.symbol];
                    return (
                      <React.Fragment key={r.symbol}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-pointer" onClick={() => toggleRow(r.symbol)}>
                          <td className="py-2 font-semibold text-gray-800 dark:text-gray-200 truncate">
                            <span className={`inline-block w-3 text-gray-400 text-[10px] transition-transform ${isOpen ? 'rotate-90' : ''}`}>▸</span> {r.symbol}
                          </td>
                          <td className={`py-2 text-right tabular-nums ${mism ? amber : 'text-gray-700 dark:text-gray-300'}`}>{fmtQty(r.ledger_qty)}</td>
                          <td className={`py-2 text-right tabular-nums ${mism ? amber : 'text-gray-700 dark:text-gray-300'}`}>{fmtQty(r.tracker_qty)}</td>
                          <td className="py-2 text-right tabular-nums text-gray-400">{Math.abs(r.reserve_qty || 0) < 1e-6 ? '—' : fmtNum(r.reserve_qty, 4)}</td>
                          <td className="py-2 text-right tabular-nums text-gray-500" title="central-book hedge basket (ours, owned by no strategy)">{Math.abs(central) < 1e-6 ? '—' : fmtQty(central)}</td>
                          <td className="py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{brokerCell(bq)}</td>
                          <td className="py-2 text-right tabular-nums text-gray-500" title="Broker − our net (Tracker+Reserve+Central): trades placed outside the system">
                            {brokerLoading && bq == null ? '…' : manual == null ? '—' : (Math.abs(manual) < 1e-4 ? '0' : `${manual > 0 ? '+' : ''}${fmtQty(manual)}`)}
                          </td>
                        </tr>
                        {isOpen && (r.strategies || []).map((s) => (
                          <tr key={`${r.symbol}-${s.strategy_id}`} className="bg-gray-50/50 dark:bg-gray-900/30">
                            <td className="py-1.5 pl-5 text-[11px] text-gray-500 dark:text-gray-400 truncate" title={s.name}>{s.name}</td>
                            <td className="py-1.5 text-right tabular-nums text-gray-500">{fmtQty(s.ledger_qty)}</td>
                            <td className="py-1.5 text-right tabular-nums text-gray-500">{fmtQty(s.tracker_qty)}</td>
                            <td className="py-1.5 text-right text-gray-400">—</td>
                            <td className="py-1.5 text-right text-gray-400">—</td>
                            <td className="py-1.5 text-right text-gray-400">—</td>
                            <td className={`py-1.5 text-right tabular-nums ${Math.abs(s.drift) > 1e-4 ? 'text-amber-500/80' : 'text-gray-400'}`} title="per-strategy Ledger − Tracker (must be 0)">
                              {Math.abs(s.drift) > 1e-6 ? `${s.drift > 0 ? '+' : ''}${fmtNum(s.drift, 2)}` : '0'}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-200 dark:border-gray-700 font-semibold text-[11px]">
                    <td className="py-2 text-gray-700 dark:text-gray-300">Total</td>
                    <td className={`py-2 text-right tabular-nums ${mismTotal ? 'text-amber-500 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'}`}>{fmtQty(totals.l)}</td>
                    <td className={`py-2 text-right tabular-nums ${mismTotal ? 'text-amber-500 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'}`}>{fmtQty(totals.t)}</td>
                    <td className="py-2 text-right tabular-nums text-gray-400">{Math.abs(totals.res) < 1e-6 ? '—' : fmtNum(totals.res, 4)}</td>
                    <td className="py-2 text-right tabular-nums text-gray-500">{Math.abs(totals.cen) < 1e-6 ? '—' : fmtQty(totals.cen)}</td>
                    <td className="py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{brokerCell(totals.b)}</td>
                    <td className="py-2 text-right tabular-nums text-gray-500">{brokerLoading && broker == null ? '…' : `${totals.b - totals.sys > 0 ? '+' : ''}${fmtQty(totals.b - totals.sys)}`}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
